import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiCheck,
  FiAlertTriangle,
  FiX,
  FiChevronDown,
  FiChevronUp,
  FiGithub,
  FiClipboard,
  FiArrowRight,
  FiLock,
  FiUpload,
  FiUserPlus,
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import {
  scoreSkill,
  fetchFromGitHub,
  GitHubFetchError,
  type ScoreReport,
} from '../lib/skillScorer';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';

type Severity = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
interface SecurityFinding {
  id?: string;
  title?: string;
  severity: Severity;
  category?: string;
  location?: string | { file?: string; start_line?: number; end_line?: number | null };
  confidence?: number;
  explanation?: string;
}
interface SecurityScanResult {
  score: number;
  severity: Severity;
  recommendation?: string;
  findings: SecurityFinding[];
  componentCount?: number;
}

type Mode = 'paste' | 'github';

const PLACEHOLDER = `---
name: your-skill-name
description: Creates X. Use when the user asks to "do Y" or mentions "Z".
---

# Your Skill Name

## Instructions

### Step 1: …

## Examples

## Troubleshooting
`;

function gradeColor(grade: ScoreReport['grade']): string {
  switch (grade) {
    case 'Excellent':
      return 'text-emerald-300';
    case 'Strong':
      return 'text-brand-300';
    case 'Decent':
      return 'text-amber-300';
    case 'Needs work':
      return 'text-orange-300';
    case 'Critical issues':
      return 'text-red-300';
  }
}

function ScoreRing({ score, grade }: { score: number; grade: ScoreReport['grade'] }) {
  const radius = 90;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (score / 100) * circ;
  const stroke =
    score >= 90 ? '#34d399' :
    score >= 75 ? '#6571ff' :
    score >= 60 ? '#fbbf24' :
    score >= 40 ? '#fb923c' :
    '#f87171';
  return (
    <div className="relative w-56 h-56" aria-label={`Score ${score} out of 100`}>
      <svg className="w-full h-full -rotate-90" viewBox="0 0 200 200">
        <circle
          cx="100"
          cy="100"
          r={radius}
          stroke="rgba(255,255,255,0.08)"
          strokeWidth="14"
          fill="transparent"
        />
        <motion.circle
          cx="100"
          cy="100"
          r={radius}
          stroke={stroke}
          strokeWidth="14"
          strokeLinecap="round"
          fill="transparent"
          strokeDasharray={circ}
          initial={{ strokeDashoffset: circ }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1.1, ease: [0.4, 0, 0.2, 1] }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="text-6xl font-bold tabular-nums">{score}</div>
        <div className={`text-sm font-semibold mt-1 ${gradeColor(grade)}`}>{grade}</div>
      </div>
    </div>
  );
}

function StatusIcon({ status }: { status: 'pass' | 'warn' | 'fail' | 'skipped' }) {
  if (status === 'pass') return <FiCheck className="text-emerald-400" />;
  if (status === 'warn') return <FiAlertTriangle className="text-amber-400" />;
  if (status === 'fail') return <FiX className="text-red-400" />;
  return <span className="text-slate-500 text-xs">·</span>;
}

function CategoryBar({ id, label, weight, earned, ratio }: ScoreReport['categories'][number]) {
  const pct = Math.round(ratio * 100);
  const color =
    pct >= 90 ? 'bg-emerald-400' :
    pct >= 70 ? 'bg-brand-500' :
    pct >= 50 ? 'bg-amber-400' :
    'bg-red-400';
  return (
    <div key={id}>
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-sm font-medium text-white">{label}</div>
        <div className="text-xs text-slate-400 tabular-nums">
          {earned.toFixed(1)} / {weight}
        </div>
      </div>
      <div className="h-2 rounded-full bg-white/5 overflow-hidden">
        <motion.div
          className={`h-full rounded-full ${color}`}
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.7, ease: [0.4, 0, 0.2, 1] }}
        />
      </div>
    </div>
  );
}

function severityColor(s: Severity): string {
  return s === 'CRITICAL' ? 'text-red-400'
    : s === 'HIGH' ? 'text-orange-300'
    : s === 'MEDIUM' ? 'text-amber-300'
    : 'text-emerald-300';
}

function severityBg(s: Severity): string {
  return s === 'CRITICAL' ? 'bg-red-500/10 border-red-500/40'
    : s === 'HIGH' ? 'bg-orange-500/10 border-orange-500/40'
    : s === 'MEDIUM' ? 'bg-amber-500/10 border-amber-500/40'
    : 'bg-emerald-500/10 border-emerald-500/40';
}

function SecurityScanPanel({
  loading, scan, error, showAll, onToggleAll,
}: {
  loading: boolean;
  scan: SecurityScanResult | null;
  error: string | null;
  showAll: boolean;
  onToggleAll: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="glass-card p-6 sm:p-8 mb-8"
    >
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="text-2xl font-bold">Security report</h2>
        <span className="text-xs text-slate-500">Independent risk analysis</span>
      </div>
      {loading && (
        <div className="flex items-center gap-3 text-sm text-slate-400">
          <div className="w-4 h-4 border-2 border-brand-500/30 border-t-brand-500 rounded-full animate-spin" />
          Running security checks. This can take up to 30 seconds for repos with many files.
        </div>
      )}
      {!loading && error && (
        <div className="text-sm text-amber-300">
          Couldn't complete the security scan: {error}
        </div>
      )}
      {!loading && scan && (
        <>
          <div className={`flex flex-col sm:flex-row items-start sm:items-center gap-4 p-4 rounded-xl border ${severityBg(scan.severity)} mb-5`}>
            <div className="flex items-baseline gap-3">
              <div className="text-4xl font-bold tabular-nums">{scan.score}</div>
              <div className="text-sm text-slate-400">risk score</div>
            </div>
            <div className={`text-sm font-semibold ${severityColor(scan.severity)}`}>{scan.severity}</div>
            {scan.recommendation && (
              <div className="text-sm text-slate-300">{scan.recommendation}</div>
            )}
            {typeof scan.componentCount === 'number' && (
              <div className="text-xs text-slate-500 sm:ml-auto">{scan.componentCount} component(s) analyzed</div>
            )}
          </div>

          {scan.findings.length === 0 ? (
            <div className="text-sm text-slate-400">No security findings.</div>
          ) : (
            <>
              <div className="text-sm text-slate-300 mb-3">
                <strong className="text-white">{scan.findings.length}</strong> finding{scan.findings.length === 1 ? '' : 's'} across this skill.
              </div>
              <div className="space-y-2">
                {(showAll ? scan.findings : scan.findings.slice(0, 5)).map((f, i) => (
                  <div key={(f.id ?? '') + i} className="flex gap-3 items-start text-sm border border-white/5 rounded-lg p-3 bg-black/20">
                    <div className={`text-xs font-bold uppercase tracking-wide ${severityColor(f.severity)} w-20 shrink-0`}>{f.severity}</div>
                    <div className="flex-1">
                      <div className="font-medium text-white">{f.title || f.id || 'Finding'}</div>
                      {f.explanation && <div className="text-slate-400 text-sm mt-0.5">{f.explanation}</div>}
                      {(f.location || f.category) && (
                        <div className="text-xs text-slate-500 mt-1">
                          {f.location && <span className="font-mono">{typeof f.location === 'string' ? f.location : `${f.location.file ?? ''}${f.location.start_line ? `:${f.location.start_line}` : ''}`}</span>}
                          {f.location && f.category && <span> · </span>}
                          {f.category && <span>{f.category}</span>}
                          {typeof f.confidence === 'number' && <span> · {Math.round(f.confidence * 100)}% confidence</span>}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {scan.findings.length > 5 && (
                <button
                  type="button"
                  onClick={onToggleAll}
                  className="mt-3 text-sm text-brand-300 hover:text-brand-200"
                >
                  {showAll ? 'Show top 5 only' : `Show all ${scan.findings.length} findings`}
                </button>
              )}
            </>
          )}
        </>
      )}
    </motion.div>
  );
}

export default function Evaluate() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>('paste');
  const [pasteValue, setPasteValue] = useState('');
  const [urlValue, setUrlValue] = useState('');
  const [report, setReport] = useState<ScoreReport | null>(null);
  const [resolved, setResolved] = useState<string | null>(null);
  const [scoredUrl, setScoredUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showAllFindings, setShowAllFindings] = useState(false);
  const [securityScan, setSecurityScan] = useState<SecurityScanResult | null>(null);
  const [securityLoading, setSecurityLoading] = useState(false);
  const [securityError, setSecurityError] = useState<string | null>(null);
  const [showAllSecurityFindings, setShowAllSecurityFindings] = useState(false);

  const handleAddToRepo = () => {
    if (!scoredUrl) return;
    sessionStorage.setItem('pendingSubmitUrl', scoredUrl);
    if (user) {
      navigate('/submit');
    } else {
      sessionStorage.setItem('postLoginRedirect', '/submit');
      navigate('/login?redirect=/submit');
    }
  };

  const fetchSecurityScan = async (payload: { repo_url?: string; skill_md?: string }) => {
    setSecurityScan(null);
    setSecurityError(null);
    setSecurityLoading(true);
    try {
      const { data } = await api.post('/security-scan', payload);
      setSecurityScan(data as SecurityScanResult);
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || 'Security scan unavailable';
      setSecurityError(msg);
    } finally {
      setSecurityLoading(false);
    }
  };

  const handlePasteEvaluate = () => {
    if (!pasteValue.trim()) {
      toast.error('Paste your SKILL.md content first.');
      return;
    }
    const result = scoreSkill({ source: 'paste', skillMd: pasteValue });
    setReport(result);
    setResolved(null);
    setScoredUrl(null);
    setShowAllFindings(false);
    requestAnimationFrame(() => {
      document.getElementById('result-section')?.scrollIntoView({ behavior: 'smooth' });
    });
    // Fire the security scan against the pasted SKILL.md content too. Both
    // assessments run for every evaluation path: upload, GitHub URL, paste.
    fetchSecurityScan({ skill_md: pasteValue });
  };

  const handleGithubEvaluate = async () => {
    if (!urlValue.trim()) {
      toast.error('Paste a GitHub URL first.');
      return;
    }
    setLoading(true);
    try {
      const fetched = await fetchFromGitHub(urlValue);
      const result = scoreSkill({
        source: 'github',
        skillMd: fetched.skillMd,
        directoryListing: fetched.directoryListing,
        folderName: fetched.folderName,
        skillFilename: fetched.skillFilename,
        subfolderEntries: fetched.subfolderEntries,
      });
      setReport(result);
      setResolved(
        `${fetched.resolved.owner}/${fetched.resolved.repo}@${fetched.resolved.branch}${
          fetched.resolved.path ? ` · ${fetched.resolved.path}` : ''
        }`,
      );
      setScoredUrl(urlValue.trim());
      setShowAllFindings(false);
      requestAnimationFrame(() => {
        document.getElementById('result-section')?.scrollIntoView({ behavior: 'smooth' });
      });
      // Auto-fire the security scan in parallel with the rendered quality score.
      fetchSecurityScan({ repo_url: urlValue.trim() });
    } catch (err) {
      if (err instanceof GitHubFetchError) {
        if (err.code === 'not_found') toast.error('Repo or path not found. Double-check the URL.');
        else if (err.code === 'rate_limited') toast.error(err.message);
        else if (err.code === 'invalid_url') toast.error(`Invalid URL: ${err.message}`);
        else toast.error(err.message);
      } else {
        toast.error('Could not fetch from GitHub.');
      }
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setReport(null);
    setResolved(null);
    setScoredUrl(null);
    setShowAllFindings(false);
    setSecurityScan(null);
    setSecurityError(null);
    setSecurityLoading(false);
    setShowAllSecurityFindings(false);
  };

  return (
    <div className="pt-24 pb-20 min-h-screen">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10"
        >
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">
            <span className="gradient-text">Evaluate your Skill.md</span>
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Get an instant score and actionable report grounded in documented best practices.
          </p>
          <div className="mt-4 inline-flex items-center gap-2 text-xs text-slate-500">
            <FiLock className="text-emerald-400" />
            Quality score runs in your browser. Security scan runs in our hardened sandbox and is not retained after the scan.
          </div>
        </motion.div>

        {/* Input zone */}
        <div className="glass-card p-6 sm:p-8 mb-10">
          {/* Tabs */}
          <div className="flex gap-2 mb-6">
            <button
              type="button"
              onClick={() => setMode('paste')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
                mode === 'paste'
                  ? 'bg-brand-500/20 text-brand-300 border border-brand-500/40'
                  : 'text-slate-400 hover:text-white border border-transparent'
              }`}
            >
              <FiClipboard /> Paste SKILL.md
            </button>
            <button
              type="button"
              onClick={() => setMode('github')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
                mode === 'github'
                  ? 'bg-brand-500/20 text-brand-300 border border-brand-500/40'
                  : 'text-slate-400 hover:text-white border border-transparent'
              }`}
            >
              <FiGithub /> GitHub URL
            </button>
          </div>

          {mode === 'paste' && (
            <>
              <label htmlFor="skill-md" className="block text-sm text-slate-400 mb-2">
                Paste the contents of your SKILL.md file
              </label>
              <textarea
                id="skill-md"
                value={pasteValue}
                onChange={(e) => setPasteValue(e.target.value)}
                placeholder={PLACEHOLDER}
                rows={20}
                spellCheck={false}
                className="w-full font-mono text-xs sm:text-sm bg-black/30 border border-white/10 rounded-xl p-4 text-slate-200 placeholder-slate-600 focus:outline-none focus:border-brand-500/60 resize-y"
              />
              <div className="flex justify-end mt-4">
                <button
                  type="button"
                  onClick={handlePasteEvaluate}
                  className="btn-gradient text-sm px-6 py-3 rounded-xl"
                >
                  <span className="inline-flex items-center gap-2">
                    Evaluate <FiArrowRight />
                  </span>
                </button>
              </div>
            </>
          )}

          {mode === 'github' && (
            <>
              <label htmlFor="gh-url" className="block text-sm text-slate-400 mb-2">
                Paste a GitHub URL. Accepts repo root, a `tree/branch/path` URL, or a blob link to SKILL.md.
              </label>
              <input
                id="gh-url"
                type="url"
                inputMode="url"
                value={urlValue}
                onChange={(e) => setUrlValue(e.target.value)}
                placeholder="https://github.com/anthropics/skills/tree/main/document-skills/pdf"
                className="w-full bg-black/30 border border-white/10 rounded-xl p-4 text-slate-200 placeholder-slate-600 focus:outline-none focus:border-brand-500/60"
              />
              <p className="text-xs text-slate-500 mt-2">
                Public repos only. Uses GitHub's public API, unauthenticated. No data leaves your
                browser through us.
              </p>
              <div className="flex justify-end mt-4">
                <button
                  type="button"
                  onClick={handleGithubEvaluate}
                  disabled={loading}
                  className="btn-gradient text-sm px-6 py-3 rounded-xl disabled:opacity-60 disabled:cursor-wait"
                >
                  <span className="inline-flex items-center gap-2">
                    {loading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Fetching…
                      </>
                    ) : (
                      <>
                        Evaluate <FiArrowRight />
                      </>
                    )}
                  </span>
                </button>
              </div>
            </>
          )}
        </div>

        {/* Result */}
        <AnimatePresence>
          {report && (
            <motion.div
              id="result-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              {resolved && (
                <p className="text-xs text-slate-500 mb-4 text-center">
                  Scoring: <span className="text-slate-300 font-mono">{resolved}</span>
                </p>
              )}

              <div className="glass-card p-6 sm:p-10 mb-8 flex flex-col md:flex-row items-center gap-8">
                <ScoreRing score={report.score} grade={report.grade} />
                <div className="flex-1 w-full">
                  <h2 className="text-2xl font-bold mb-1">Score breakdown</h2>
                  <p className="text-sm text-slate-400 mb-6">
                    Six categories weighted by importance for skill effectiveness.
                  </p>
                  <div className="space-y-4">
                    {report.categories.map((c) => (
                      <CategoryBar key={c.id} {...c} />
                    ))}
                  </div>
                </div>
              </div>

              {/* Submit-to-repo CTA */}
              {report.score >= 75 && scoredUrl && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                  className="glass-card p-6 sm:p-8 mb-8 border border-emerald-500/20 bg-gradient-to-br from-emerald-500/[0.07] to-brand-500/[0.05]"
                >
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
                    <div className="w-14 h-14 rounded-2xl bg-emerald-500/15 flex items-center justify-center text-2xl text-emerald-300 shrink-0">
                      <FiUpload />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-white mb-1">
                        Looking good. Add it to the directory?
                      </h3>
                      <p className="text-sm text-slate-400">
                        {user
                          ? 'Publish this skill so others can discover and install it.'
                          : 'Create a free account to publish this skill to the directory.'}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleAddToRepo}
                      className="btn-gradient text-sm px-6 py-3 rounded-xl whitespace-nowrap"
                    >
                      <span className="inline-flex items-center gap-2">
                        {user ? (
                          <>Submit to directory <FiArrowRight /></>
                        ) : (
                          <>Sign up to submit <FiUserPlus /></>
                        )}
                      </span>
                    </button>
                  </div>
                </motion.div>
              )}

              {/* Paste-only encouragement to provide a URL */}
              {report.score >= 75 && !scoredUrl && (
                <div className="glass-card p-5 mb-8 border border-brand-500/15 text-sm text-slate-300 flex flex-col sm:flex-row items-start sm:items-center gap-3">
                  <FiUpload className="text-brand-400 text-lg shrink-0" />
                  <div className="flex-1">
                    Great score. Push this SKILL.md to a public GitHub repo, then re-evaluate
                    with the GitHub URL tab to submit it to the directory.
                  </div>
                  <button
                    type="button"
                    onClick={() => setMode('github')}
                    className="text-xs text-brand-300 hover:text-brand-200 underline"
                  >
                    Use GitHub URL tab
                  </button>
                </div>
              )}

              {/* Security scan panel */}
              {(securityLoading || securityScan || securityError) && (
                <SecurityScanPanel
                  loading={securityLoading}
                  scan={securityScan}
                  error={securityError}
                  showAll={showAllSecurityFindings}
                  onToggleAll={() => setShowAllSecurityFindings((v) => !v)}
                />
              )}

              {/* Top recommendations */}
              {report.recommendations.length > 0 && (
                <div className="mb-8">
                  <h2 className="text-2xl font-bold mb-4">Top recommendations</h2>
                  <div className="space-y-3">
                    {report.recommendations.map((rec) => (
                      <div
                        key={rec.id}
                        className="glass-card p-5 flex gap-4 items-start"
                      >
                        <div className="text-xl mt-0.5">
                          <StatusIcon status={rec.severity === 'fail' ? 'fail' : 'warn'} />
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold text-white mb-1">{rec.title}</div>
                          <div className="text-sm text-slate-400 mb-2">{rec.why}</div>
                          <div className="text-sm text-brand-200 bg-brand-500/10 border border-brand-500/20 rounded-lg px-3 py-2">
                            <span className="text-brand-400 font-medium">Fix: </span>
                            {rec.fix}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Full findings */}
              <div className="glass-card p-6 mb-8">
                <button
                  type="button"
                  onClick={() => setShowAllFindings((v) => !v)}
                  className="flex items-center justify-between w-full text-left"
                >
                  <span className="text-lg font-semibold">
                    All {report.findings.length} checks
                  </span>
                  {showAllFindings ? <FiChevronUp /> : <FiChevronDown />}
                </button>
                {showAllFindings && (
                  <div className="mt-4 divide-y divide-white/5">
                    {report.findings.map((f) => (
                      <div key={f.id} className="py-3 flex gap-3 items-start text-sm">
                        <div className="mt-0.5"><StatusIcon status={f.status} /></div>
                        <div className="flex-1">
                          <div className="font-medium text-white">{f.title}</div>
                          <div className="text-slate-400">{f.message}</div>
                          {f.fix && f.status !== 'pass' && (
                            <div className="text-xs text-brand-300 mt-1">Fix: {f.fix}</div>
                          )}
                        </div>
                        <div className="text-xs text-slate-600 font-mono whitespace-nowrap">
                          {f.id}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  type="button"
                  onClick={reset}
                  className="px-6 py-3 rounded-xl border border-white/10 text-sm font-medium hover:bg-white/5"
                >
                  Evaluate another
                </button>
                <Link
                  to="/browse"
                  className="btn-gradient text-sm px-6 py-3 rounded-xl text-center"
                >
                  <span className="inline-flex items-center gap-2">
                    Browse top-scored skills <FiArrowRight />
                  </span>
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
